import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import PublicSite from './components/PublicSite';
import AdminPage from './components/admin/AdminPage';
import HostDashboardPage from './components/admin/HostDashboardPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import RSVPPublicModule from './components/RSVPPublicModule';
import TravelPage from './pages/TravelPage';
import ThingsToDoPage from './pages/ThingsToDoPage';
import CalendarPage from './pages/CalendarPage';
import DesignLab from './pages/DesignLab';
import ExperiencePage from './pages/ExperiencePage';
import InviteLandingPage from './pages/InviteLandingPage';
import InvitationFilmPage from './pages/InvitationFilmPage';
import SponsorsPage from './pages/SponsorsPage';
import GuestbookPage from './pages/guestbook/GuestbookPage';
import { isRsvpEnabled, isPhase2Released } from './utils/featureGates';
import { useParams } from 'react-router-dom';

// Public seating-collaboration page — lazy so three.js/r3f stay out of the main bundle.
const SeatingCollab = React.lazy(() => import('./components/admin/auto-seating/SeatingCollab'));
// Celebration guest hub (July 2026) — post-invitation personalized site, token-gated.
// ADDITIVE: the protected /experience + /invite flows are untouched. Lazy-split.
const CelebrationPage = React.lazy(() => import('./pages/celebration/CelebrationPage'));
// The pre-paint body color for /celebration lives in public/index.html (it must
// run before first paint; anything here is already too late). This fallback
// keeps the chunk-loading gap dark too (was null → cream shell showed through).
const CelebrationFallback = () => (
  <div style={{ position: 'fixed', inset: 0, background: '#171021' }} aria-hidden="true" />
);
// LOCAL PREVIEW ONLY — renders the real AutoSeatingPage standalone for testing against a mock
// backend. Gated by REACT_APP_SEATING_PREVIEW so it is inert in every normal/production build.
const SeatingPreview = React.lazy(() => import('./components/admin/auto-seating'));

// Redirect /rsvp/:token → /invite/:token (backward compat for old guest links)
const RsvpTokenRedirect = () => {
  const { token } = useParams();
  return <Navigate to={`/invite/${token}`} replace />;
};

// 404 Component - Minimal, no hints about hidden features
const NotFound = () => (
  <div style={{ 
    minHeight: '100vh', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    background: '#fef7e0',
    fontFamily: 'system-ui, sans-serif'
  }}>
    <div style={{ textAlign: 'center' }}>
      <h1 style={{ fontSize: 48, color: '#1f2937', margin: 0 }}>404</h1>
      <p style={{ color: '#6b7280', marginTop: 8 }}>Page not found</p>
    </div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicSite />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/host-dashboard" element={<HostDashboardPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Video Guestbook — public guest recording flow (Oscar). Gated by a valid
            event token inside the page/backend; no admin login. */}
        <Route path="/guestbook/:eventToken" element={<GuestbookPage />} />
        
        {/* 
          PHASE 2 GATED ROUTES
          When PHASE_2_RELEASE=false, these routes return 404.
          When PHASE_2_RELEASE=true, routes become active.
        */}
        {/* DO NOT USE IN GUEST JOURNEY — /rsvp/:token is NOT the RSVP destination.
            Guest flow always uses /experience/:token for RSVP.
            This route is kept only so old links don't 404. */}
        <Route path="/rsvp"        element={<RSVPPublicModule />} />
        <Route path="/rsvp/:token" element={<RSVPPublicModule />} />
        
        {/* Explore Section Routes - Gated by Phase 2 */}
        <Route 
          path="/travel" 
          element={isPhase2Released() ? <TravelPage /> : <NotFound />} 
        />
        <Route 
          path="/things-to-do" 
          element={isPhase2Released() ? <ThingsToDoPage /> : <NotFound />} 
        />
        <Route 
          path="/calendar" 
          element={isPhase2Released() ? <CalendarPage /> : <NotFound />} 
        />
        
        {/* Admin Design Lab — Sandbox only, no live data */}
        <Route path="/admin/design-lab" element={<DesignLab />} />

        {/*
          INVITE LANDING — Official Invitation card page.
          Bypasses all gates. Token optional.
          /invite          → no guest name shown
          /invite/:token   → guest/family name overlaid on card
        */}
        <Route path="/invite"        element={<InviteLandingPage />} />
        <Route path="/invite/:token" element={<InviteLandingPage />} />

        {/*
          EXPERIENCE — Personalized token invite flow.
          Bypasses ALL gates: no maintenance screen, no entry splash, no feature flags.
          Accessible at /experience/:token where token = party_rsvp_token
        */}
        <Route path="/experience/:token" element={<ExperiencePage />} />

        {/*
          INVITATION FILM — Cinematic video experience triggered by "View Official Invitation".
          Flow: orient video → landscape Play button → main film → RSVP
        */}
        <Route path="/film/:token" element={<InvitationFilmPage />} />

        {/* SPONSORS PAGE */}
        <Route path="/sponsors" element={<SponsorsPage />} />

        {/* CELEBRATION HUB — personalized post-invitation guest site. Token-gated by a
            party-level celebration_token (NOT the rsvp_token); invalid token = private 404,
            never a fallback to the invitation experience. */}
        <Route path="/celebration/:celebrationToken" element={<React.Suspense fallback={<CelebrationFallback />}><CelebrationPage /></React.Suspense>} />
        <Route path="/celebration" element={<React.Suspense fallback={<CelebrationFallback />}><CelebrationPage /></React.Suspense>} />

        {/* SEATING COLLABORATION — public, revocable token. Privacy-bounded layout editor;
            collaborators propose arrangements that return to the host as restorable versions. */}
        <Route path="/seating/collab/:token" element={<React.Suspense fallback={null}><SeatingCollab /></React.Suspense>} />

        {/* LOCAL PREVIEW ONLY — real Auto Seating component standalone (never in production builds).
            Wrapped in the admin's house background so it matches where Auto Seating actually lives. */}
        {process.env.REACT_APP_SEATING_PREVIEW === 'true' && (
          <Route path="/seating/preview" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
              <div style={{
                minHeight: '100vh', padding: '24px 16px',
                // House depth: white "sphere" base + soft pearl orbs (cool blue, rose, sage) and a
                // restrained gold whisper — the frosted-glass cards read against this. No beige.
                background: [
                  'radial-gradient(34% 30% at 84% 15%, rgba(170,188,208,.22), transparent 70%)',
                  'radial-gradient(30% 26% at 13% 20%, rgba(206,176,196,.18), transparent 70%)',
                  'radial-gradient(42% 38% at 80% 92%, rgba(190,198,170,.16), transparent 72%)',
                  'radial-gradient(36% 30% at 9% 88%, rgba(198,167,90,.06), transparent 72%)',
                  'radial-gradient(circle at 38% 24%, #ffffff 0%, #f4f6fa 48%, #e9edf3 78%, #e2e7ef 100%)',
                ].join(','),
                backgroundAttachment: 'fixed',
              }}>
                <SeatingPreview />
              </div>
            </React.Suspense>} />
        )}

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
